import React, { useState, useEffect } from 'react'
import { Box, Grid, Typography, Button, Select, MenuItem, Dialog, DialogTitle, DialogContent, DialogActions, CircularProgress, Paper, Container, Backdrop } from '@mui/material'
import MessageSidebar from '../../components/messaging/MessageSidebar'
import UtteranceList from '../../components/messaging/UtteranceList'
import { getBackendUserUrl, getCurrentUser } from '../../utils/utils'
import axios from 'axios'

function MessagesPage({ messageTags, setMessageTags }) {
    const [user, setUser] = useState(null)
    const [events, setEvents] = useState([])
    const [selectedEvent, setSelectedEvent] = useState(null)
    const [chats, setChats] = useState([])
    const [selectedConversation, setSelectedConversation] = useState(null)
    const [loading, setLoading] = useState(true)
    const [groupChatModalOpen, setGroupChatModalOpen] = useState(false)

    // Fetch user and their events
    useEffect(() => {
        async function fetchUserAndEvents() {
            setLoading(true)
            const userData = await getCurrentUser()
            setUser(userData)
            if (userData?.userId) {
                const eventsRes = await axios.get(`${getBackendUserUrl()}/event/events_by_userId/${userData.userId}`)
                setEvents(eventsRes.data)
                if (eventsRes.data.length > 0) {
                    setSelectedEvent(eventsRes.data[0])
                } else {
                    setSelectedEvent(null)
                }
            }
            setLoading(false)
        }
        fetchUserAndEvents()
    }, [])

    // Fetch chats for selected event (mock for now)
    useEffect(() => {
        if (!selectedEvent) {
            setChats([])
            setSelectedConversation(null)
            return
        }
        // TODO: Replace with backend call for event chats
        const mockChats = [
            {
                id: 'group1',
                name: 'All Hosts',
                participants: selectedEvent?.teamMembers || [],
                lastMessage: 'Welcome to the event!',
                timestamp: Date.now(),
                unread: false,
                messages: [
                    { senderId: user?.userId, content: 'Welcome to the event!', created_at: new Date().toISOString() }
                ]
            },
            ...((selectedEvent?.addons || []).map((addon, idx) => ({
                id: `vendor${idx}`,
                name: `${addon.company?.name} (${addon.addonType})`,
                participants: [user, addon.company],
                lastMessage: 'Vendor chat started',
                timestamp: Date.now(),
                unread: false,
                messages: [
                    { senderId: user?.userId, content: `Hello ${addon.company?.name}!`, created_at: new Date().toISOString() }
                ]
            })) || [])
        ]
        setChats(mockChats)
        setSelectedConversation(mockChats[0] || null)
    }, [selectedEvent, user])

    const handleEventChange = (e) => {
        const event = events.find(ev => ev.eventId === e.target.value)
        setSelectedEvent(event)
    }

    const handleOpenGroupChatModal = () => setGroupChatModalOpen(true)
    const handleCloseGroupChatModal = () => setGroupChatModalOpen(false)

    return (
        <Box sx={{ minHeight: '100vh', background: '#f7f8fa', py: 6 }}>
            <Backdrop open={loading} sx={{ zIndex: 2000, color: '#fff' }}>
                <CircularProgress color="inherit" />
            </Backdrop>
            <Box
                sx={{
                    maxWidth: 800,
                    width: '100%',
                    mx: 'auto',
                    borderRadius: 4,
                    boxShadow: '0 4px 32px 0 rgba(80, 80, 120, 0.10)',
                    background: '#fff',
                    minHeight: '75vh',
                    display: 'flex',
                    overflow: 'hidden',
                }}
            >
                <Grid container sx={{ flex: 1, minHeight: '75vh' }}>
                    {/* Sidebar */}
                    <Grid item xs={4} md={4} lg={3} sx={{
                        background: '#f7f8fa',
                        borderRight: '1px solid #e5e7eb',
                        display: 'flex',
                        flexDirection: 'column',
                        minWidth: 0,
                        height: 'auto',
                        px: 0,
                        py: 0,
                    }}>
                        <Box sx={{ p: 3, borderBottom: '1px solid #e5e7eb', background: '#f7f8fa' }}>
                            <Typography variant="h6" fontWeight={700} sx={{ mb: 1 }}>
                                Select Event
                            </Typography>
                            {events.length > 0 ? (
                                <Select
                                    value={selectedEvent?.eventId || ''}
                                    onChange={handleEventChange}
                                    fullWidth
                                    size="small"
                                    sx={{
                                        mb: 2,
                                        background: '#fff',
                                        borderRadius: 2,
                                        '& .MuiOutlinedInput-notchedOutline': {
                                            borderColor: '#e5e7eb',
                                        },
                                        '&:hover .MuiOutlinedInput-notchedOutline': {
                                            borderColor: '#6366F1',
                                        },
                                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                            borderColor: '#4F46E5',
                                        },
                                    }}
                                >
                                    {events.map(ev => (
                                        <MenuItem key={ev.eventId} value={ev.eventId}>{ev.title || ev.eventName}</MenuItem>
                                    ))}
                                </Select>
                            ) : (
                                <Typography color="text.secondary" sx={{ my: 2 }}>
                                    No events found. Join or create an event to start messaging.
                                </Typography>
                            )}
                        </Box>
                        <Box sx={{ flex: 1, overflowY: 'auto', pb: 2 }}>
                            {selectedEvent ? (
                                chats.length > 0 ? (
                                    <MessageSidebar
                                        onSelectConversation={setSelectedConversation}
                                        selectedConversation={selectedConversation}
                                        events={[{ ...selectedEvent, conversations: chats }]}
                                    />
                                ) : (
                                    <Box sx={{ p: 3, color: 'text.secondary', textAlign: 'center' }}>
                                        <Typography variant="body1" sx={{ mb: 1 }}>
                                            No conversations yet
                                        </Typography>
                                        <Typography variant="body2">
                                            Start a new group chat to begin collaborating!
                                        </Typography>
                                    </Box>
                                )
                            ) : (
                                <Box sx={{ p: 3, color: 'text.secondary', textAlign: 'center' }}>
                                    <Typography variant="body1" sx={{ mb: 1 }}>
                                        Select an Event
                                    </Typography>
                                    <Typography variant="body2">
                                        Choose an event to view its conversations
                                    </Typography>
                                </Box>
                            )}
                        </Box>
                        <Box sx={{ p: 3, borderTop: '1px solid #e5e7eb', background: '#f7f8fa' }}>
                            <Button
                                variant="outlined"
                                color="primary"
                                fullWidth
                                sx={{
                                    borderRadius: 3,
                                    textTransform: 'none',
                                    fontWeight: 600,
                                    py: 1.2,
                                    borderColor: '#4F46E5',
                                    color: '#4F46E5',
                                    background: 'transparent',
                                    boxShadow: 'none',
                                    '&:hover': {
                                        borderColor: '#6366F1',
                                        color: '#6366F1',
                                        background: 'rgba(79,70,229,0.04)',
                                    },
                                }}
                                onClick={handleOpenGroupChatModal}
                                disabled={events.length === 0}
                            >
                                + New Group Chat
                            </Button>
                        </Box>
                    </Grid>
                    {/* Chat Area */}
                    <Grid item xs={8} md={8} lg={9} sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        background: '#fff',
                        minWidth: 0,
                        height: 'auto',
                        px: 0,
                        py: 0,
                    }}>
                        {selectedEvent ? (
                            selectedConversation ? (
                                <UtteranceList
                                    conversation={selectedConversation}
                                    onTagClick={() => {}}
                                    tags={messageTags}
                                    onAddTag={(tag) => setMessageTags([...messageTags, tag])}
                                    onRemoveTag={(tag) => setMessageTags(messageTags.filter((t) => t.label !== tag.label))}
                                    sender={user}
                                    receiver={selectedConversation.participants?.find(p => p.userId !== user?.userId)}
                                />
                            ) : (
                                <Box sx={{ p: 3, color: 'text.secondary', textAlign: 'center', flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <Box>
                                        <Typography variant="h6" sx={{ mb: 1, color: '#232946' }}>
                                            Select a Conversation
                                        </Typography>
                                        <Typography variant="body1">
                                            Choose a conversation from the sidebar or start a new group chat
                                        </Typography>
                                    </Box>
                                </Box>
                            )
                        ) : (
                            <Box sx={{ p: 3, color: 'text.secondary', textAlign: 'center', flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <Box>
                                    <Typography variant="h6" sx={{ mb: 1, color: '#232946' }}>
                                        No Event Selected
                                    </Typography>
                                    <Typography variant="body1">
                                        Please select an event to view its conversations
                                    </Typography>
                                </Box>
                            </Box>
                        )}
                    </Grid>
                </Grid>
                {/* Group Chat Creation Modal (placeholder) */}
                <Dialog open={groupChatModalOpen} onClose={handleCloseGroupChatModal}>
                    <DialogTitle>Create New Group Chat</DialogTitle>
                    <DialogContent>
                        <Typography>Group chat creation coming soon...</Typography>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleCloseGroupChatModal}>Close</Button>
                    </DialogActions>
                </Dialog>
            </Box>
        </Box>
    )
}

export default MessagesPage
